import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { params } from '@app/configurations';
import { filterObject } from '@app/core/utilities/object.utilities';
import { shareRef } from '@app/shared/operators/share-ref.operator';
import { Store } from '@ngxs/store';
import {
  ApiAccessLevel,
  ApiEndpoint,
  ApiGetListRequest,
  ApiPatchResponse,
  ApiRoute,
  CustomerApiRoute,
} from '../models/api.models';
import { CustomerSummary } from '../models/common.models';
import { Customer } from '../models/customer.models';
import { StateService } from '../services/state.service';
import { CUSTOMER_STATE_TOKEN } from '../store/states/customer/customer-state.models';
import { ApiGetMutator } from './api-get-mutator.class';
import { ApiInstanceService } from './api-instance.service';
import { ApiListMutator } from './api-list-mutator.class';
import { ApiObjectMutator } from './api-object-mutator.class';
import { ApiService } from './api.service';
import { ThirdPartyAccount } from '@app/settings/shared/modules/integrations/integrations.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerApiService extends ApiInstanceService {
  readonly customerRoute: CustomerApiRoute | undefined;

  readonly endpoint: ApiEndpoint = ApiEndpoint.Customers;

  protected get customerId(): number {
    return (
      this.ss.getState()[params.CUSTOMER_ID] || this.store.selectSnapshot(CUSTOMER_STATE_TOKEN)?.details?.customerId
    );
  }

  protected get apiRoute(): ApiRoute {
    return [this.customerId, this.customerRoute].filter((route) => !!route);
  }

  constructor(
    api: ApiService,
    private ss: StateService,
    protected store: Store,
  ) {
    super(api);
  }

  getCustomerById(customerId: number, accessLevel?: ApiAccessLevel): Observable<Customer | undefined> {
    return this.getObject<Customer>({ route: customerId, accessLevel }).pipe(
      map((response) => response.data),
      catchError(() => of(undefined)),
    );
  }

  getCustomersById(customerIds: number[]): Observable<Customer[]> {
    return this.getList<Customer>({
      route: [],
      filterValues: { customerId: customerIds },
    }).pipe(
      map((response) => response.data),
      catchError(() => of([])),
    );
  }

  getCustomersCount(): Observable<number> {
    return this.getList<Customer>({
      route: [],
      limit: 1,
      include: ['customerId'],
      filterValues: { archived: false },
    }).pipe(
      map((response) => response.meta?.pagination?.total || 0),
      catchError(() => of(0)),
    );
  }

  getCustomerSummary(customerId: number): Observable<CustomerSummary> {
    return this.getObject<CustomerSummary>({
      route: [customerId, CustomerApiRoute.Summary],
    }).pipe(
      map(({ data }) => {
        data.campaignOptimizationTypes = data.campaignOptimizationTypes?.sort().reverse();
        return data;
      }),
      catchError(() => of({} as CustomerSummary)),
    );
  }

  getCustomerThirdPartyAccounts(
    customerId?: number,
    req?: Partial<ApiGetListRequest<ThirdPartyAccount>>,
  ): Observable<ThirdPartyAccount[]> {
    customerId ||= this.customerId;

    const filterValues = {
      ...req?.filterValues,
      customerId,
    };

    return this.getAll<ThirdPartyAccount>({
      route: [customerId, CustomerApiRoute.ThirdPartyAccounts],
      ...req,
      filterValues,
    }).pipe(
      map((response) =>
        response.map((account) => ({
          ...account,
          createdAt: account.createdAt ? new Date(account.createdAt) : null,
          archivedAt: account.archivedAt ? new Date(account.archivedAt) : null,
        })),
      ),
      catchError(() => of([])),
    );
  }

  hasSDVCPMCampaigns(customerId: number): Observable<boolean> {
    return this.getObject<CustomerSummary>({
      route: [customerId, 'summary'],
      filterValues: <any>{
        campaignType: ['AMAZON-SPONSORED-DISPLAY'],
        campaignOptimizationType: ['VCPM'],
      },
    }).pipe(map(({ data }) => data.campaignCount > 0));
  }

  getCustomerSignupData(): Observable<any> {
    return this.getObject({
      endpoint: ApiEndpoint.Signup,
      route: ['customer'],
    });
  }

  getCustomerListMutator<T extends object>(
    customer$: Observable<Customer>,
    mutatorCall: (customerId: number) => ApiListMutator<T>,
  ): Observable<ApiListMutator<T>> {
    return this.getCustomerMutator(customer$, mutatorCall);
  }

  getCustomerObjectMutator<T extends object>(
    customer$: Observable<Customer>,
    mutatorCall: (customerId: number) => ApiObjectMutator<T>,
  ): Observable<ApiObjectMutator<T>> {
    return this.getCustomerMutator(customer$, mutatorCall);
  }

  getFirstCustomer(): Observable<Customer> {
    return this.getList<Customer>({
      sortValues: { name: 1 },
      limit: 1,
      filterValues: { archived: false },
      route: [],
    }).pipe(map((customers) => customers.data?.[0]));
  }

  updateCustomer(customer: Partial<Customer>): Observable<ApiPatchResponse<Customer>> {
    return this.patch<Customer>({ data: filterObject(customer) });
  }

  userHasNotificationsAccess(): Observable<boolean> {
    return this.getList<Customer>({
      filterValues: { notifications: true },
      limit: 1,
      include: ['name'],
      route: [],
    }).pipe(map((d) => !!d.data.length));
  }

  private getCustomerMutator<T extends ApiGetMutator<any, any, any>>(
    customer$: Observable<Customer>,
    mutatorCall: (customerId: number) => T,
  ): Observable<T> {
    return customer$.pipe(
      map((customer) => (customer ? mutatorCall(customer.customerId) : void 0)),
      filter((mutator) => !!mutator),
      shareRef(),
    );
  }
}
